/**
 * What a phone does before the first render, in this order (native sub-plan,
 * PR 8): restore the session, switch the outbox on, refill the ledger, render.
 *
 * 1. **Restore the session** from its Keychain/Keystore copy if the WebView
 *    lost localStorage — otherwise the boot below would see a stranger.
 * 2. **Mirror every write** into the native outbox from here on, and let sync
 *    acknowledge accepted rows.
 * 3. **Refill** the ledger from the outbox: whatever IndexedDB lost, and
 *    whatever a widget or Siri logged while the app was closed.
 *
 * Nothing here can stop the app opening. Every failure is reported (INV-7) and
 * the render goes ahead, because the numpad must open (INV-4) — a lost refill
 * leaves the rows in the outbox for the next launch, not gone.
 *
 * A no-op on the web: no mirror, no outbox.
 */

import type { NativeBridge, OutboxEntry } from "@/native/bridge";

import { markSignedOut, signOutPending } from "./sign_out_marker";

import { readSession, recoverPendingSessionWrite, restoreSessionFromMirror, setSessionMirror } from "./backend";
import { clearLocalData, getMeta, setMeta, localLedgerAccountId } from "./db";
import { FAMILY_ID_KEY } from "./sync";
import {
  type OutboxStore,
  type RefillReport,
  importOutboxEntries,
  outboxStoreFrom,
  refillFromOutbox,
  startNativeOutbox,
} from "./outbox";

function report(what: string, result: RefillReport): void {
  if (result.foreign > 0 || result.unreadable > 0) {
    console.warn(
      `${what}: ${String(result.imported)} restored, ${String(result.foreign)} from another account ` +
        `left untouched, ${String(result.unreadable)} unreadable and kept.`,
    );
  }
}

/** Whose ledger this is right now: the session's account, else the ledger's own owner. */
async function currentOwner(): Promise<string | null> {
  if (signOutPending()) return null;
  return readSession()?.accountId ?? (await localLedgerAccountId());
}

function warn(what: string): (cause: unknown) => void {
  return (cause) => {
    console.warn(`${what}:`, cause instanceof Error ? cause.message : cause);
  };
}

/** Run before `createRoot`. Resolves when the app may render — always. */
export async function prepareNativeBoot(bridge: NativeBridge): Promise<void> {
  if (bridge.platform === "web") return;

  setSessionMirror({
    get: () => bridge.sessionGet(),
    set: (session) => bridge.sessionSet(session),
    clear: () => bridge.sessionClear(),
  });

  try { await recoverPendingSessionWrite(); }
  catch (cause) {
    warn("Could not finish native sign-in; cleanup is blocked to protect this ledger")(cause);
    return;
  }

  let pending = signOutPending();
  if (!pending) {
    try {
      pending = await bridge.signOutPending();
    } catch (cause) {
      warn("Could not check the native sign-out barrier; session restoration is blocked")(cause);
      return;
    }
  }
  try {
    if (pending) {
      markSignedOut();
      await bridge.markSignedOut();
      await bridge.scheduleNotifications([]);
      await bridge.writeSnapshot({ signedOut: true });
      await bridge.clearAll();
      await clearLocalData();
    }
  } catch (cause) {
    markSignedOut();
    warn("Sign-out cleanup failed; this device remains signed out")(cause);
  }
  await restoreSessionFromMirror().catch(warn("Could not restore the session from its native copy"));

  if (!bridge.hasOutbox) return;
  const store: OutboxStore = outboxStoreFrom(bridge);
  await startNativeOutbox(store);

  const owner = await currentOwner().catch((cause: unknown) => {
    warn("Could not tell whose ledger this is; the outbox waits for the next launch")(cause);
    return null;
  });
  if (owner !== null) {
    const familyOwner = await bridge.familyOwner?.(owner).catch((cause: unknown) => {
      warn("Could not read the native family owner; family rows remain queued")(cause);
      return null;
    });
    // Restore the lifecycle scope too: a removed member must reach copy/discard,
    // even when eviction lost the family profile and membership display cache.
    try {
      if (familyOwner && await getMeta(FAMILY_ID_KEY) === null) await setMeta(FAMILY_ID_KEY, familyOwner);
    } catch (cause) { warn("Could not restore family recovery scope; native rows remain queued")(cause); }
    await refillFromOutbox(store, owner, familyOwner)
      .then((result) => {
        report("Outbox refill", result);
      })
      .catch(warn("Could not refill the ledger from the native outbox"));
  }

  // A widget or intent logging while the app is open lands the same way.
  bridge.onNativeRowsLogged((entries: readonly OutboxEntry[]) => {
    void currentOwner()
      .then(async (now) => {
        if (now === null) return;
        report("Natively logged rows", await importOutboxEntries(entries, now, store, await bridge.familyOwner?.(now)));
      })
      .catch(warn("Could not bring natively logged rows into the ledger"));
  });
}
